import { Combobox, Transition } from "@headlessui/react";
import clsx from "clsx";
import InputLabel from "components/InputLabel";
import React from "react";
import { useTranslation } from "react-i18next";
import Input from "components/Input";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSearch,
} from "@tabler/icons-react";

export type Option = {
  id: string;
  name: string;
  category?: string;
  children?: Option[];
};

const DynamicDropdown = (props: {
  containerClassName?: string;
  pageSize?: number;
  disabled?: boolean;
  onSelect?: (item: Option | undefined) => void;
  placeholder?: string;
  children?: React.ReactNode;
  labelName?: string;
  helper?: string;
  required?: boolean;
  options?: Option[];
  fetchOptions?: (
    searchTerm: string,
    page?: number,
    pageSize?: number
  ) => Promise<{ items: Option[]; count: number }>;
  createOption?: (name: string) => Promise<void | Option>;
  value?: string;
}) => {
  const { t } = useTranslation();
  const [optionList, setOptionList] = React.useState<{
    items: Option[];
    count: number;
  }>({ items: [], count: 0 });
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    props.value ?? ""
  );
  const [loading, setLoading] = React.useState(false);
  const optionsListRef = React.useRef<HTMLUListElement>(null);
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [optionsOpen, setOptionsOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [hasMoreOptions, setHasMoreOptions] = React.useState(false);
  const [fetchLoading, setFetchLoading] = React.useState(false);
  const [newItemAdded, setNewItemAdded] = React.useState(false);
  const [currentCategory, setCurrentCategory] = React.useState<Option | null>(
    null
  );

  React.useEffect(() => {
    const timeout = setTimeout(async () => {
      await updateItems(searchTerm as unknown as string);
      optionsListRef.current?.scrollTo({ top: 0 });
      setFocusedIndex(-1);
      setCurrentCategory(null);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  React.useEffect(() => {
    if (optionsOpen) {
      setCurrentCategory(null);
      updateItems(searchTerm);
    } else {
      setOptionList({ items: [], count: 0 });
    }
  }, [optionsOpen]);

  React.useEffect(() => {
    props.value && props.value != searchTerm && setSearchTerm(props.value);
  }, [props.value]);

  async function updateItems(searchTerm: string = "") {
    setLoading(true);
    await props
      .fetchOptions?.(searchTerm, 1, Number(props.pageSize))
      .then((res) => {
        setOptionList(res);
        setHasMoreOptions(res.items.length < Number(res.count));
      });
    setLoading(false);
  }
  const onClickOption = async (item: Option) => {
    if (item.id === "search_term") {
      setNewItemAdded(true);
      const newItem = await props.createOption?.(item.name);
      if (newItem) {
        props.onSelect?.(newItem);
        setNewItemAdded(true);
      }
      updateItems(item.name);
    } else {
      if (item.children?.length) {
        inputRef.current?.focus();
        onClickCategory(item);
      } else {
        props.onSelect?.(item);
      }
    }
  };

  const onClickCategory = (item: Option) => {
    if (!item.children?.length) return;
    setCurrentCategory(item);
    setOptionList({ items: item.children, count: item.children.length });
    setFocusedIndex(-1);
  };

  function onArrowDown() {
    setFocusedIndex((prev) =>
      prev === optionList.items.length - 1 ? -1 : prev + 1
    );
  }

  function onArrowUp() {
    setFocusedIndex((prev) => (prev <= 0 ? -1 : Number(prev) - 1));
  }

  function focusToOptionsList() {
    setTimeout(() => {
      optionsListRef.current?.focus();
      optionsListRef.current?.scrollTo({ top: 0 });
    }, 1);
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      !optionsOpen && buttonRef.current?.click();
      focusToOptionsList();
      onArrowDown();
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      focusToOptionsList();
      onArrowUp();
      return;
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      onClickBack();
      return;
    }

    if (
      e.key === "ArrowRight" &&
      optionList.items[focusedIndex].children?.length
    ) {
      e.preventDefault();
      onClickCategory(optionList.items[focusedIndex]);
      optionsListRef.current?.scrollTo({ top: 0 });
      return;
    }

    if (e.key === "Enter") {
      searchTerm && focusedIndex === -1
        ? onClickOption({
            id: "search_term",
            name: searchTerm,
          })
        : onClickOption(optionList.items[focusedIndex]);
      return;
    }
    setFocusedIndex(-1);
  };

  const onBlurInput = () => {
    setFocusedIndex(-1);
    setCurrentCategory(null);

    if (newItemAdded) {
      setNewItemAdded(false);
      return;
    }

    const timer = setTimeout(() => {
      if (!props.value) {
        setSearchTerm("");
      }

      clearTimeout(timer);
    }, 200);
  };

  const loadMoreOptions = async () => {
    setFetchLoading(true);
    const response = await props.fetchOptions?.(
      String(searchTerm),
      optionList.items.length / Number(props.pageSize) + 1,
      Number(props.pageSize)
    );
    if (response) {
      const updatedItems = [...optionList.items, ...response.items];
      setHasMoreOptions(updatedItems.length < Number(response.count));
      setOptionList({
        count: response.count,
        items: updatedItems,
      });
    }
    setFetchLoading(false);
  };

  const onOptionsScroll = async (
    e: React.UIEvent<HTMLUListElement> | React.KeyboardEvent
  ) => {
    e.stopPropagation();
    if (!hasMoreOptions || fetchLoading) return;
    if (e.target) {
      const { scrollHeight, scrollTop, clientHeight } =
        e.target as HTMLUListElement;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        await loadMoreOptions();
      }
    }
  };

  const onOptionsKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.code === "ArrowDown") {
      onArrowDown();
    } else if (e.code === "ArrowUp") {
      onArrowUp();
      focusedIndex === -1 && inputRef.current?.focus();
    } else if (e.code === "Enter") {
      const focusedItem = optionList.items[focusedIndex];
      onClickOption(focusedItem);
      if (!focusedItem.children?.length) {
        inputRef.current?.click(); // to close the dropdown
      }
      return;
    } else if (e.code === "ArrowRight") {
      onClickCategory(optionList.items[focusedIndex]);
      optionsListRef.current?.scrollTo({ top: 0 });
    } else if (e.code === "ArrowLeft") {
      onClickBack();
    }

    onOptionsScroll(e);
  };

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e as unknown as string);
    props.onSelect?.(undefined);
  };

  const onClickBack = () => {
    setCurrentCategory(null);
    props.options &&
      setOptionList({ items: props.options, count: props.options.length });
  };

  const categoryHeader = () => {
    return (
      <div
        className={clsx(
          "text-gray-500 flex items-center pl-2 pr-4 py-[6px] border-b border-gray-300 cursor-pointer bg-gray-50 group/menu relative",
          "min-w-64"
        )}
        onClick={onClickBack}
      >
        <IconChevronLeft
          size={24}
          className="group-hover/menu:text-indigo-700"
        />
        <div className="text-center text-sm font-semibold w-full">
          {currentCategory?.name}
        </div>
      </div>
    );
  };

  function optionsInfoTitle() {
    return (
      <div
        className={clsx(
          "bg-gray-50 whitespace-nowrap w-full border-gray-300 text-xs font-medium py-1 px-4 text-gray-500",
          { "border-b border-b-gray-200": optionList.items.length }
        )}
      >
        {t("productEditor.selectOrCreateOption")}
      </div>
    );
  }

  function searchTermOption() {
    if (optionList.items.find((item) => item.name === searchTerm)) return;
    return (
      <Combobox.Option
        key="search_term"
        id="search_term"
        value={searchTerm}
        className={clsx(
          "flex !cursor-pointer group items-center text-gray-700 hover:bg-indigo-50 px-4 py-2 hover:text-indigo-700",
          {
            "bg-indigo-50 text-indigo-700": focusedIndex === -1 && searchTerm,
          }
        )}
        onClick={() =>
          onClickOption({
            id: "search_term",
            name: searchTerm ?? "",
          })
        }
      >
        <div className="flex justify-between gap-4 w-full">
          <span className="whitespace-nowrap">{`“${searchTerm}” ${t(
            "productEditor.create"
          )}`}</span>
          <span
            className={clsx(
              "whitespace-nowrap text-gray-500 text-sm font-normal",
              { "text-indigo-500": focusedIndex === -1 }
            )}
          >
            {t("commons.pressEnter")}
          </span>
        </div>
      </Combobox.Option>
    );
  }

  function optionItem(index: number, item: Option) {
    return (
      <Combobox.Option
        key={index}
        value={item}
        id={item.id}
        className={clsx(
          "flex !cursor-pointer group items-center text-gray-700 hover:bg-indigo-50 pl-4 pr-2 py-2 hover:text-indigo-700",
          "justify-between gap-6 text-sm font-medium",
          {
            "bg-indigo-50 text-indigo-700": focusedIndex === index,
          }
        )}
        onClick={() => onClickOption(item)}
      >
        <div>
          {item.name}
          {!currentCategory && (
            <div
              className={clsx(
                "font-normal text-gray-500 group-hover:text-indigo-500",
                {
                  "text-indigo-500": focusedIndex === index,
                }
              )}
            >
              {item.category}
            </div>
          )}
        </div>
      </Combobox.Option>
    );
  }

  function categoryItem(index: number, item: Option) {
    return (
      <div
        key={index}
        className={clsx(
          "flex !cursor-pointer group items-center text-gray-700 hover:bg-indigo-50 pl-4 pr-2 py-2 hover:text-indigo-700",
          "justify-between gap-6",
          {
            "bg-indigo-50 text-indigo-700": focusedIndex === index,
          }
        )}
        onClick={() => onClickCategory(item)}
      >
        <span>{item.name}</span>
        {item.children?.length && (
          <IconChevronRight className="text-gray-700" size={20} />
        )}
      </div>
    );
  }

  function helperText() {
    return (
      <div className={clsx("text-sm leading-5 font-normal pt-2 text-gray-500")}>
        {props.helper}
      </div>
    );
  }

  return (
    <div className={clsx(props.containerClassName, "w-full relative")}>
      {props.labelName ? (
        <InputLabel
          required={props.required}
          labelName={props.labelName ?? ""}
        />
      ) : null}
      <Combobox>
        {({ open }) => {
          React.useEffect(() => {
            setOptionsOpen(open);
            if (!open) {
              onBlurInput();
            }
          }, [open]);

          return (
            <>
              <div
                className={clsx(
                  "w-full pb-0 !mb-0 border border-gray-300 rounded-md h-9 overflow-hidden flex items-center focus-within:border-indigo-600"
                )}
              >
                <Combobox.Input
                  as={Input}
                  value={searchTerm ?? ""}
                  className={clsx(
                    "flex items-center w-full min-w-full !min-h-full border-none rounded-none overflow-hidden",
                    "bg-transparent pl-2 !py-0 placeholder:text-sm"
                  )}
                  containerClassName="!mb-0 w-full !pb-0 flex-grow"
                  onChange={onChangeInput}
                  placeholder={props.placeholder}
                  onKeyDown={handleKeyDown}
                  ref={inputRef}
                  onClick={() => buttonRef.current?.click()}
                  onDoubleClick={() => inputRef.current?.select()}
                />

                <Combobox.Button
                  className="px-2 h-full flex items-center"
                  ref={buttonRef}
                >
                  <IconSearch />
                </Combobox.Button>
              </div>

              <Transition
                as="div"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options
                  autoFocus
                  className={clsx(
                    "absolute z-10 my-1 min-w-72 overflow-y-auto",
                    "max-h-[396px] overflow-auto rounded-md bg-white text-base !p-0",
                    "shadow-lg border border-gray-300 focus:outline-none sm:text-sm",
                    { "cursor-not-allowed": props.disabled }
                  )}
                  ref={optionsListRef}
                  onKeyDown={onOptionsKeyDown}
                  onScroll={onOptionsScroll}
                >
                  {props.createOption && optionsInfoTitle()}
                  {currentCategory && categoryHeader()}
                  {props.createOption && searchTerm && searchTermOption()}

                  {loading
                    ? skeleton()
                    : optionList.items.map((item, index) =>
                        item.children?.length
                          ? categoryItem(index, item)
                          : optionItem(index, item)
                      )}
                </Combobox.Options>
              </Transition>
            </>
          );
        }}
      </Combobox>

      {props.helper && helperText()}
    </div>
  );
};

export default DynamicDropdown;

function skeleton() {
  const divs = new Array(9)
    .fill(null)
    .map((_, index) => (
      <div
        key={index}
        className="h-7 w-full bg-gray-200 rounded-lg dark:bg-gray-700 my-1"
      ></div>
    ));

  return <div className="animate-pulse px-4 py-2">{divs}</div>;
}
